// src/prototype/enterprise/decisions/decisionsModel.ts
// ============================================================
// ENTERPRISE · DECISIONS — build the unified decision timeline from the spine (read-only).
//
// Merges two adapter sources into one newest-first list:
//   · getDecisionLog()  → the attributed governance decisions (APPROVE/DEFER/KILL/HOLD).
//   · getScenarios()    → each saved scenario surfaced as a first-class SAVED event (as in the
//                         design). The spine scenario has no author, so the save is attributed
//                         to the demo enterprise persona (flagged) and its rationale is derived
//                         from the scenario's own levers (cap change + weight direction).
//
// Everything here is display mapping over adapter data — no spine write.
// ============================================================

import { getDecisionLog, getScenarios, getRuns, getRunCap } from '../../data/adapters/enterpriseSpine';
import type { DecisionValue, UseCaseStatusValue } from '../../data/adapters/enterpriseSpine';
import { initiativeName } from '../portfolio/portfolioDisplay';

export type DecisionKind = DecisionValue | 'SAVED';

export interface DecisionEntry {
  id: string;
  kind: 'decision' | 'saved';
  use_case_id: number | null;
  title: string;
  decision: DecisionKind;
  run_id?: string;
  runLabel: string; // e.g. "Q2 Budget Reduction" or "base: Q1 2026 Baseline Planning"
  rationale: string;
  by: string; // "name · role"
  statusChange: string; // "Before → After" (or "scenario + scoring_run created")
  date: string; // "Feb 05"
  year: string; // "2026"
  decided_at: string; // ISO — sort key
  hasEvo: boolean; // decisions link to the initiative evolution view; saves don't
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso: string) => `${MONTHS[+iso.slice(5, 7) - 1]} ${iso.slice(8, 10)}`;
const fmtYear = (iso: string) => iso.slice(0, 4);

const STATUS_LABEL: Record<UseCaseStatusValue, string> = {
  CANDIDATE: 'Candidate',
  SHORTLISTED: 'Shortlisted',
  APPROVED: 'Approved',
  FUNDED: 'Funded',
  DEFERRED: 'Deferred',
  ON_HOLD: 'Hold',
  KILLED: 'Killed',
};

/** Derive a save rationale from the scenario's own levers (cap change + weight direction). */
function saveRationale(baseCap: number, newCap: number, deltas: Record<string, number>): string {
  const capBit = `Capital cap $${baseCap.toFixed(1)}M → $${newCap.toFixed(1)}M`;
  const names: Record<string, string> = {
    financial_impact: 'Financial Impact',
    operational_urgency: 'Operational Urgency',
    strategic_alignment: 'Strategic Alignment',
    implementation_feasibility: 'Implementation Feasibility',
  };
  const raised = Object.keys(deltas).filter((k) => deltas[k] > 0).map((k) => names[k]);
  const lowered = Object.keys(deltas).filter((k) => deltas[k] < 0).map((k) => names[k]);
  const weightBit = raised.length
    ? `${raised.join(' & ')} weight raised${lowered.length ? `, ${lowered.join(' & ')} lowered` : ''}`
    : 'strategic weights unchanged';
  return `${capBit}; ${weightBit}. Levers captured to the scenario record.`;
}

/** The full, merged, newest-first decision timeline. */
export function buildDecisionTimeline(): DecisionEntry[] {
  const runs = getRuns();
  const runLabel = (id?: string) => (id ? runs.find((r) => r.run_id === id)?.label ?? '' : '');

  const decisions: DecisionEntry[] = getDecisionLog().map((d) => ({
    id: d.decision_id,
    kind: 'decision',
    use_case_id: d.use_case_id,
    title: initiativeName(d.title),
    decision: d.decision,
    run_id: d.run_id,
    runLabel: runLabel(d.run_id),
    rationale: d.rationale,
    // The hero's (uc 1555) Q2 approval is a two-step governance: CFO gate then committee
    // ratification. The spine's decided_by records only the CFO gate, so the committee step
    // is added at the display layer for this decision (uc 1555, APPROVE, under the Q2 scenario).
    by:
      d.use_case_id === 1555 && d.decision === 'APPROVE' && d.scenario_id
        ? `${d.decided_by.name} · CFO gate → committee ratified`
        : `${d.decided_by.name} · ${d.decided_by.role}`,
    statusChange: `${STATUS_LABEL[d.status_before]} → ${STATUS_LABEL[d.status_after]}`,
    date: fmtDate(d.decided_at),
    year: fmtYear(d.decided_at),
    decided_at: d.decided_at,
    hasEvo: true,
  }));

  const saves: DecisionEntry[] = getScenarios().map((s) => ({
    id: s.scenario_id,
    kind: 'saved',
    use_case_id: null,
    title: `Scenario "${s.name}" saved`,
    decision: 'SAVED',
    run_id: s.base_run_id,
    runLabel: `base: ${s.base_run_label ?? runLabel(s.base_run_id)}`,
    rationale: saveRationale(getRunCap(s.base_run_id), s.levers.budget_cap, s.levers.weight_deltas as unknown as Record<string, number>),
    by: 'D. Whitfield · VP Enterprise Strategy',
    statusChange: 'scenario + scoring_run created',
    date: fmtDate(s.created_at),
    year: fmtYear(s.created_at),
    decided_at: s.created_at,
    hasEvo: false,
  }));

  return [...decisions, ...saves].sort((a, b) => b.decided_at.localeCompare(a.decided_at));
}

// ── display styling (mirrors the design) ─────────────────────────────────────

/** Badge pill classes per decision kind. */
export const DECISION_BADGE: Record<DecisionKind, string> = {
  APPROVE: 'bg-[#15803d] text-white',
  DEFER: 'bg-[#e2e8f0] text-[#475569]',
  HOLD: 'bg-[#e2e8f0] text-[#475569]',
  KILL: 'bg-[#fee2e2] text-[#b91c1c]',
  SAVED: 'bg-[#fef3c7] text-[#b45309]',
};

/** Timeline dot color per decision kind. */
export const DECISION_DOT: Record<DecisionKind, string> = {
  APPROVE: '#15803d',
  DEFER: '#64748b',
  HOLD: '#64748b',
  KILL: '#b91c1c',
  SAVED: '#d97706',
};

/** Filter option lists derived from the timeline. */
export function decisionFilterOptions(entries: DecisionEntry[]): {
  initiatives: { id: number; title: string }[];
  runs: { id: string; label: string }[];
} {
  const initiatives = new Map<number, string>();
  const runsMap = new Map<string, string>();
  for (const e of entries) {
    if (e.use_case_id != null) initiatives.set(e.use_case_id, e.title);
    if (e.run_id) runsMap.set(e.run_id, getRuns().find((r) => r.run_id === e.run_id)?.label ?? e.run_id);
  }
  return {
    initiatives: [...initiatives.entries()].map(([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title)),
    runs: [...runsMap.entries()].map(([id, label]) => ({ id, label })),
  };
}
